/**
 * NAV chart series preparation for the SQUAD factsheet.
 *
 * Two concerns the raw API series does not cover:
 *
 * 1. Frequency. Below the threshold every trading day is drawn; above it the
 *    series is thinned to weekly Friday closes. SQUAD's requirement, and it keeps
 *    a 20-year curve from putting ~5,500 points on 371 pt of paper.
 * 2. Axis. The chart uses an ordinal x-axis so non-trading days cannot show up as
 *    plateaus, which means the tick labels have to be built here — on real calendar
 *    boundaries rather than every n-th index.
 */

/** Raw point as delivered by the history API. */
export type SquadNavPoint = [number, number];

/** Above this span the series is thinned to weekly closes. */
export const SQUAD_WEEKLY_THRESHOLD_YEARS = 5;

const DAY_MS = 86_400_000;
const YEAR_MS = 365.25 * DAY_MS;

const FRIDAY = 5;

/**
 * ISO weekday: Monday 1 … Sunday 7.
 * getUTCDay() numbers Sunday as 0, which would rank it *before* Monday — but weekKey
 * puts a Sunday in the week that started the preceding Monday, so it is that week's
 * last day. Sunday quotes are rare but real (seven of them in the SQUAD Value series).
 */
const isoWeekday = (date: Date): number => date.getUTCDay() || 7;

const toDate = (point: SquadNavPoint) => new Date(Number(point[0]));

/** ISO week key, so a week is not split across a year boundary. */
function weekKey(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // Thursday determines the ISO week-numbering year
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
  return `${d.getUTCFullYear()}-${String(week).padStart(2, '0')}`;
}

/** Span of the series in years. */
export function squadNavSpanYears(points: SquadNavPoint[]): number {
  if (!Array.isArray(points) || points.length < 2) return 0;
  return (Number(points[points.length - 1][0]) - Number(points[0][0])) / YEAR_MS;
}

/**
 * Daily below the threshold, weekly Friday closes above it.
 *
 * Weeks without a Friday quote (holiday) fall back to the last trading day of that
 * week, so no week is dropped. First and last point are always kept — the last one
 * is the as-of date of the factsheet and must not be rounded away.
 */
export function staffSquadNavFrequency(
  points: SquadNavPoint[],
  thresholdYears: number = SQUAD_WEEKLY_THRESHOLD_YEARS,
): SquadNavPoint[] {
  if (!Array.isArray(points) || points.length < 3) return points || [];
  if (squadNavSpanYears(points) <= thresholdYears) return points;

  const byWeek = new Map<string, SquadNavPoint>();
  points.forEach((point) => {
    const date = toDate(point);
    if (Number.isNaN(date.getTime())) return;
    const key = weekKey(date);
    const kept = byWeek.get(key);
    if (!kept) {
      byWeek.set(key, point);
      return;
    }
    const keptDay = isoWeekday(toDate(kept));
    const day = isoWeekday(date);
    // Prefer Friday; otherwise the latest day in that week
    if (keptDay === FRIDAY) return;
    if (day === FRIDAY || day > keptDay) byWeek.set(key, point);
  });

  const weekly = Array.from(byWeek.values()).sort((a, b) => Number(a[0]) - Number(b[0]));
  const first = points[0];
  const last = points[points.length - 1];
  if (weekly[0]?.[0] !== first[0]) weekly.unshift(first);
  if (weekly[weekly.length - 1]?.[0] !== last[0]) weekly.push(last);
  return weekly;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/** Tick granularity that keeps roughly 7–11 labels on the axis. */
function tickStep(spanYears: number): { unit: 'year' | 'month'; every: number } {
  // Every fourth year left only five labels on the 22-year series — too sparse to
  // place a point in time; every third gives SQUAD the two more they asked for.
  if (spanYears > 16) return { unit: 'year', every: 3 };
  if (spanYears > 8) return { unit: 'year', every: 2 };
  if (spanYears > 3) return { unit: 'year', every: 1 };
  if (spanYears > 1.5) return { unit: 'month', every: 6 };
  if (spanYears > 0.75) return { unit: 'month', every: 3 };
  return { unit: 'month', every: 1 };
}

/** Day-level label for series too short to contain a calendar boundary. */
const dayLabel = (date: Date) =>
  `${String(date.getUTCDate()).padStart(2, '0')}.${String(date.getUTCMonth() + 1).padStart(2, '0')}.`;

/**
 * Fallback for series that never cross a month boundary — a fund in its first weeks.
 * Without it the ordinal axis carries no label at all, because labels are tied to
 * calendar boundaries. Places a few day-level ticks at fixed positions instead.
 */
function fallbackCategories(points: SquadNavPoint[]): string[] {
  const out = points.map(() => '');
  const n = points.length;
  if (n === 0) return out;

  const positions =
    n <= 3
      ? [n - 1]
      : [
          Math.max(1, Math.round(n * 0.12)),
          Math.round(n * 0.5),
          Math.min(n - 1, Math.round(n * 0.88)),
        ];

  new Set(positions).forEach((index) => {
    if (index < 0 || index >= n) return;
    const date = toDate(points[index]);
    if (!Number.isNaN(date.getTime())) out[index] = dayLabel(date);
  });
  return out;
}

/**
 * Categories for the ordinal x-axis: one entry per data point, but only calendar
 * boundaries carry a label — the rest are empty strings so ApexCharts draws nothing.
 *
 * Ticks therefore sit on real year/month starts ("2020", "Jan 25") instead of at
 * every n-th index, which is what makes the axis readable at any span.
 */
export function buildSquadNavCategories(points: SquadNavPoint[]): string[] {
  if (!Array.isArray(points) || points.length === 0) return [];
  const spanYears = squadNavSpanYears(points);
  const { unit, every } = tickStep(spanYears);

  const labelFor = (date: Date) =>
    unit === 'year'
      ? String(date.getUTCFullYear())
      : `${MONTHS_SHORT[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(2)}`;

  // Keep the first ticks clear of the y-axis labels
  const leadIn = Math.max(1, Math.round(points.length * 0.03));

  let previous: Date | null = null;
  const categories = points.map((point, index) => {
    const date = toDate(point);
    if (Number.isNaN(date.getTime())) return '';

    const isBoundary =
      previous === null || index < leadIn
        ? false
        : unit === 'year'
          ? date.getUTCFullYear() !== previous.getUTCFullYear() &&
            date.getUTCFullYear() % every === 0
          : date.getUTCMonth() !== previous.getUTCMonth() && date.getUTCMonth() % every === 0;

    previous = date;
    return isBoundary ? labelFor(date) : '';
  });

  // A single label is as unusable as none — a short series can put its only
  // boundary right at the edge, where the lead-in guard drops it.
  return categories.filter(Boolean).length >= 2 ? categories : fallbackCategories(points);
}

/**
 * Y-axis bounds from the data instead of ApexCharts' automatic scale.
 *
 * SQUAD's layout document of 11.09.2026 marks the axis "Skalierung optimieren (auch
 * wenn es ins negative läuft)" and shows two reference charts: one running 50–750 %
 * in steps of 50, one running 95–120 % in fine steps. Both hug the data and neither
 * anchors on zero. The point is that a reader should be able to take values off the
 * chart, not just see a shape.
 *
 * So rather than padding a range and rounding it outward — which overshoots, because
 * rounding outward on a coarse step throws away exactly the space that was meant to
 * be saved — every candidate step is tried and scored on how little of the plot it
 * wastes. Ties go to the candidate nearest TARGET_TICKS gridlines. On the 22-year
 * SQUAD Value series this picks 0–650 % in steps of 50 where the old code picked
 * 0–800 %, cutting wasted height from 22 % to 4 %, and it reproduces the step size of
 * SQUAD's own reference chart.
 */

/** 1 / 2 / 2.5 / 5 / 10 × 10^n — the step sizes a reader reads off an axis without effort. */
const STEP_MULTIPLES = [1, 2, 2.5, 5, 10];
const MIN_TICKS = 8;
const MAX_TICKS = 16;
const TARGET_TICKS = 12;
/**
 * Steps are searched two decades either side of the span, so a step always exists
 * inside the tick window whatever the magnitude — the fallback below is for safety,
 * not for real data.
 */
const EXPONENT_REACH = 2;

/**
 * A dip far below half a percent of the span would otherwise cost a whole gridline:
 * the SQUAD European Convictions series touches -0.29 % before climbing to +282 %.
 * Below this threshold the bound is flattened onto zero — under a pixel at factsheet
 * size, while a genuine drawdown keeps its own gridline.
 */
const NEGLIGIBLE_EXCURSION = 0.005;

/**
 * Decimals the axis labels need so a label states the value its gridline sits on.
 * A step of 2.5 or 0.25 is a legitimate choice for a short series, but rounding those
 * labels to whole percent prints "3 %" against a line at 2.5 % — or, below a step of
 * 1, several identical labels in a row.
 */
function decimalsForStep(step: number): number {
  for (let decimals = 0; decimals <= 3; decimals += 1) {
    const scaled = step * 10 ** decimals;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9) return decimals;
  }
  return 3;
}

export interface SquadNavYRange {
  min: number;
  max: number;
  /** Number of intervals — there is one more gridline than this. */
  tickAmount: number;
  /** Decimals the labels must carry to match their gridline. */
  decimals: number;
}

export function squadNavYRange(values: number[]): SquadNavYRange | null {
  const finite = (values || []).filter((v) => Number.isFinite(v));
  if (finite.length === 0) return null;

  const rawMin = Math.min(...finite);
  const rawMax = Math.max(...finite);
  if (rawMax === rawMin) {
    return { min: rawMin - 1, max: rawMax + 1, tickAmount: 2, decimals: 0 };
  }

  const negligible = (rawMax - rawMin) * NEGLIGIBLE_EXCURSION;
  const dataMin = Math.abs(rawMin) <= negligible ? 0 : rawMin;
  const dataMax = Math.abs(rawMax) <= negligible ? 0 : rawMax;
  const span = dataMax - dataMin;

  let best: { min: number; max: number; tickAmount: number; waste: number } | null = null;
  const centre = Math.floor(Math.log10(span));
  for (let exponent = centre - EXPONENT_REACH; exponent <= centre + EXPONENT_REACH; exponent += 1) {
    for (const multiple of STEP_MULTIPLES) {
      const step = multiple * 10 ** exponent;
      if (!Number.isFinite(step) || step <= 0) continue;
      const min = Math.floor(dataMin / step) * step;
      const max = Math.ceil(dataMax / step) * step;
      if (max <= min) continue;
      const tickAmount = Math.round((max - min) / step);
      if (tickAmount + 1 < MIN_TICKS || tickAmount + 1 > MAX_TICKS) continue;
      // Round before comparing: two steps can waste the same space to floating noise
      const waste = Number((1 - span / (max - min)).toFixed(4));
      const better =
        !best ||
        waste < best.waste ||
        (waste === best.waste &&
          Math.abs(tickAmount + 1 - TARGET_TICKS) < Math.abs(best.tickAmount + 1 - TARGET_TICKS));
      if (better) best = { min, max, tickAmount, waste };
    }
  }

  // No step lands inside the tick window (a span of extreme magnitude) — fall back to
  // an even split of the padded range rather than leaving the axis to ApexCharts.
  if (!best) {
    const pad = span / (TARGET_TICKS - 1) / 2;
    const min = dataMin - pad;
    const max = dataMax + pad;
    return {
      min,
      max,
      tickAmount: TARGET_TICKS - 1,
      decimals: decimalsForStep((max - min) / (TARGET_TICKS - 1)),
    };
  }
  return {
    min: best.min,
    max: best.max,
    tickAmount: best.tickAmount,
    decimals: decimalsForStep((best.max - best.min) / best.tickAmount),
  };
}
